<template>
    <AppDialog v-model="isOpen" size="lg" @close="resetForm">
        <template #header>
            <h2 class="text-lg font-semibold text-theme-base">
                Crear producto
            </h2>
        </template>

        <div class="space-y-4">
            <custom-input
                v-model="form.name"
                label="Nombre del producto"
                :error="
                    !isNameValid && touched.name ? 'El nombre es requerido' : ''
                "
                @blur="touched.name = true"
            />

            <input-money-component
                v-model="form.price"
                enabled
                label="Precio de venta"
                :message-error="
                    !isPriceValid && touched.price
                        ? 'El precio de venta debe ser mayor a $0'
                        : ''
                "
                @blur="touched.price = true"
            />

            <barcode-scanner-input v-model="form.sku" enabled check />

            <div>
                <p class="text-xs text-theme-muted mb-2">
                    Fotos del producto (opcional)
                </p>
                <images-manage
                    id="editor-new-product-images"
                    v-model="form.images"
                    message="del producto"
                    default-size="squareImage"
                    :default-alt="'Imagen de ' + (form.name || 'producto')"
                    :default-filters="{ size: 'squareImage' }"
                    enabled
                />
            </div>
        </div>

        <template #footer>
            <div class="flex flex-col sm:flex-row justify-end gap-2">
                <button
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="isOpen = false"
                >
                    Cancelar
                </button>

                <app-button
                    v-if="!publishOnly"
                    color="success"
                    border
                    :disabled="!canSubmit"
                    :loading="isSaving"
                    @click="submit(false)"
                >
                    Crear producto
                </app-button>

                <app-button
                    color="success"
                    :disabled="!canSubmit"
                    :loading="isSaving"
                    @click="submit(true)"
                >
                    Crear y publicar
                </app-button>
            </div>
        </template>
    </AppDialog>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import { mapActions, mapGetters, mapState } from "vuex";

import { titleRegex } from "@/common/lib/regex";

import AppDialog from "@/components/utils/AppDialog.vue";
import BarcodeScannerInput from "@/components/utils/BarcodeScannerInput.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImagesManage from "@/components/utils/ImagesManage.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";

const DEFAULT_FORM = () => ({ name: "", price: 1000, sku: "", images: [] });

export default {
    name: "CreateProductModal",
    components: {
        AppDialog,
        BarcodeScannerInput,
        CustomInput,
        ImagesManage,
        InputMoneyComponent
    },
    mixins: [planLimitError],
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        // Al crear desde el editor de bloques el producto entra a un
        // segment, y un producto sin publicar no se resuelve ahí: se ofrece
        // solo "Crear y publicar".
        publishOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: DEFAULT_FORM(),
            touched: { name: false, price: false },
            isSaving: false
        };
    },
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("commons", ["selectedMerchant"]),
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        isNameValid() {
            return titleRegex.test(this.form.name);
        },
        isPriceValid() {
            return this.form.price > 0;
        },
        canSubmit() {
            return this.isNameValid && this.isPriceValid;
        }
    },
    methods: {
        ...mapActions("products", ["recordProduct"]),
        resetForm() {
            this.form = DEFAULT_FORM();
            this.touched = { name: false, price: false };
        },
        async submit(active) {
            this.touched = { name: true, price: true };
            if (!this.canSubmit || this.isSaving) return;

            this.isSaving = true;
            try {
                const payload = {
                    name: this.form.name,
                    price: this.form.price,
                    active,
                    sku: this.form.sku || undefined,
                    images: this.form.images.length
                        ? this.form.images.map((img) => ({
                              id: img.id,
                              sequence: img.sequence.sequence
                          }))
                        : undefined
                };
                if (this.selectedMerchant?.merchantId) {
                    payload.merchantId = this.selectedMerchant.merchantId;
                    payload.merchantType = this.selectedMerchant.merchantType;
                }

                const created = await this.recordProduct(payload);
                this.isSaving = false;
                this.isOpen = false;
                this.$sound.play("success");
                // El endpoint no resuelve las imágenes: se arma un
                // references[0] sintético con las del formulario para poder
                // mostrar el producto sin recargar.
                this.$emit("created", {
                    ...created,
                    references: [
                        {
                            ...(created.references?.[0] || {}),
                            images: this.form.images.map((img) => ({
                                url: img.url
                            }))
                        }
                    ]
                });
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Producto creado",
                    text: active
                        ? "El producto ha sido creado y publicado en tu catálogo."
                        : "El producto ha sido añadido a tu catálogo (sin publicar).",
                    icon: "success"
                });
            } catch (error) {
                this.isSaving = false;
                if (this.openPlanUpgradeForLimit(error)) {
                    this.isOpen = false;
                    return;
                }
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
